import React, { useState } from 'react';
import { Settings, Database, RefreshCw, Bell, Info, Webhook, Code, Key } from 'lucide-react';
import { useRealtimeTest } from '../hooks/useRealtimeTest';
import { FirebaseAuth } from '../components/FirebaseAuth';
import { DatabaseTables } from '../components/DatabaseTables';
import { FirebaseTables } from '../components/FirebaseTables';
import { WebhookSettings } from '../components/WebhookSettings';
import { ApiDocumentation } from '../components/ApiDocumentation';
import { ApiTokens } from '../components/ApiTokens';
import { useSidebarState } from '../hooks/useSidebarState';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

type TabType = 'auth' | 'databases' | 'notifications' | 'version' | 'webhooks' | 'api' | 'tokens';

export function SettingsPage() {
  const { isCollapsed } = useSidebarState();
  const [activeTab, setActiveTab] = useState<TabType>('auth');
  const [version, setVersion] = useState('1.0.0');

  const tabs = [
    { id: 'auth' as TabType, label: 'Autenticação', icon: Settings },
    { id: 'databases' as TabType, label: 'Bancos de Dados', icon: Database },
    { id: 'notifications' as TabType, label: 'Notificações', icon: Bell },
    { id: 'webhooks' as TabType, label: 'Webhooks', icon: Webhook },
    { id: 'api' as TabType, label: 'API', icon: Code },
    { id: 'tokens' as TabType, label: 'Tokens', icon: Key },
    { id: 'version' as TabType, label: 'Versão', icon: Info }
  ];

  return (
    <div className="p-8">
      <div className={`mx-auto transition-all duration-300 ${
        isCollapsed 
          ? 'max-w-[1400px] px-4' 
          : 'max-w-[1200px]'
      }`}>
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-primary/10 rounded-lg">
            <Settings className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Configurações</h1>
            <p className="text-muted-foreground text-sm">
              Gerencie as configurações do sistema
            </p>
          </div>
        </div>

        {/* Tabs */}
        <Card>
          <CardHeader>
            <Tabs defaultValue={tabs[0].id} onValueChange={(value) => setActiveTab(value as TabType)}>
              <TabsList className="w-full justify-start">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <TabsTrigger
                      key={tab.id}
                      value={tab.id}
                      className="flex items-center gap-2"
                    >
                      <Icon className="w-4 h-4" />
                      {tab.label}
                    </TabsTrigger>
                  );
                })}
              </TabsList>

              {/* Autenticação */}
              <TabsContent value="auth">
                <CardTitle className="mb-4">Autenticação</CardTitle>
                <FirebaseAuth />
              </TabsContent>

              {/* Bancos de Dados */}
              <TabsContent value="databases">
                <div className="space-y-8">
                  <DatabaseTables />
                  <FirebaseTables />
                </div>
              </TabsContent>

              {/* Notificações */}
              <TabsContent value="notifications">
                <CardTitle className="mb-4">Notificações</CardTitle>
                <div className="space-y-4">
                  <Card>
                    <CardContent className="flex items-center justify-between p-6">
                      <div>
                        <p className="font-medium">Notificações por e-mail</p>
                        <p className="text-sm text-muted-foreground">
                          Receba atualizações importantes por e-mail
                        </p>
                      </div>
                      <Switch />
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="flex items-center justify-between p-6">
                      <div>
                        <p className="font-medium">Notificações no sistema</p>
                        <p className="text-sm text-muted-foreground">
                          Receba alertas dentro do sistema
                        </p>
                      </div>
                      <Switch defaultChecked />
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              {/* Webhooks */}
              <TabsContent value="webhooks">
                <WebhookSettings />
              </TabsContent>

              {/* API */}
              <TabsContent value="api">
                <ApiDocumentation />
              </TabsContent>

              {/* Tokens */}
              <TabsContent value="tokens">
                <ApiTokens />
              </TabsContent>

              {/* Versão */}
              <TabsContent value="version">
                <CardTitle className="mb-4">Versão do Sistema</CardTitle>
                <div className="space-y-4">
                  <Card>
                    <CardContent className="p-6">
                      <label className="block text-sm text-muted-foreground mb-2">
                        Versão atual
                      </label>
                      <div className="flex gap-4">
                        <Input
                          value={version}
                          onChange={(e) => setVersion(e.target.value)}
                          placeholder="Ex: 1.0.0"
                        />
                        <Button onClick={() => alert('Versão atualizada com sucesso!')}>
                          Salvar versão
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </CardHeader>
        </Card>
      </div>
    </div>
  );
}